/**
 * Default apparatus/units derived from DCVFD February 2026 roster data.
 * These serve as fallback defaults; active apparatus are fetched from the API
 * via /api/admin/apparatus at runtime (FR-ADMIN-02).
 *
 * Unit naming convention:
 *   A  = Ambulance
 *   E  = Engine
 *   M  = Medic
 *   TW = Tower (Truck/Ladder)
 *   R  = Rescue
 *   U  = Utility
 *   B  = Brush
 *   F suffix = Flex unit (hidden from normal selection)
 *   BF suffix = Battalion Flex unit (hidden from normal selection)
 *
 * Command units (chief officer vehicles):
 *   BC  = Battalion Chief Buggy (e.g., BC505)
 *   SAF = Safety Officer Unit (e.g., SAF505)
 *   C   = Chief (e.g., C510, C513, C518, C520)
 *   AC  = Assistant Chief (e.g., AC510, AC513, AC518, AC520)
 *
 * Station mapping (derived from last digits of unit ID):
 *   510 = Station 10, 513 = Station 13, 518 = Station 18, 520 = Station 20
 */
export const APPARATUS = [
  { id: 'A510', type: 'ambulance', station: '10', label: 'Ambulance 510' },
  { id: 'A510F', type: 'ambulance', station: '10', label: 'Ambulance 510F' },
  { id: 'A513', type: 'ambulance', station: '13', label: 'Ambulance 513' },
  { id: 'A513F', type: 'ambulance', station: '13', label: 'Ambulance 513F' },
  { id: 'A518', type: 'ambulance', station: '18', label: 'Ambulance 518' },
  { id: 'B518', type: 'brush', station: '18', label: 'Brush 518' },
  { id: 'A520', type: 'ambulance', station: '20', label: 'Ambulance 520' },
  { id: 'E510', type: 'engine', station: '10', label: 'Engine 510' },
  { id: 'E510F', type: 'engine', station: '10', label: 'Engine 510F' },
  { id: 'E510BF', type: 'engine', station: '10', label: 'Engine 510BF' },
  { id: 'E513', type: 'engine', station: '13', label: 'Engine 513' },
  { id: 'E513F', type: 'engine', station: '13', label: 'Engine 513F' },
  { id: 'E518', type: 'engine', station: '18', label: 'Engine 518' },
  { id: 'E520', type: 'engine', station: '20', label: 'Engine 520' },
  { id: 'M510', type: 'medic', station: '10', label: 'Medic 510' },
  { id: 'M513', type: 'medic', station: '13', label: 'Medic 513' },
  { id: 'M518', type: 'medic', station: '18', label: 'Medic 518' },
  { id: 'M520', type: 'medic', station: '20', label: 'Medic 520' },
  { id: 'R510', type: 'rescue', station: '10', label: 'Rescue 510' },
  { id: 'TW513', type: 'tower', station: '13', label: 'Tower 513' },
  { id: 'TW513F', type: 'tower', station: '13', label: 'Tower 513F' },
  { id: 'U510', type: 'utility', station: '10', label: 'Utility 510' },
  { id: 'U513', type: 'utility', station: '13', label: 'Utility 513' },
  { id: 'U518', type: 'utility', station: '18', label: 'Utility 518' },
  { id: 'U520', type: 'utility', station: '20', label: 'Utility 520' },
] as const;

/**
 * Command / Chief Officer units — separate from station apparatus.
 * These are only shown to users with officer-level access or above.
 */
export const COMMAND_APPARATUS = [
  { id: 'BC505', type: 'buggy', station: 'command', label: 'Battalion Chief 505' },
  { id: 'SAF505', type: 'safety', station: 'command', label: 'Safety 505' },
  { id: 'C510', type: 'chief', station: '10', label: 'Chief 510' },
  { id: 'C513', type: 'chief', station: '13', label: 'Chief 513' },
  { id: 'C518', type: 'chief', station: '18', label: 'Chief 518' },
  { id: 'C520', type: 'chief', station: '20', label: 'Chief 520' },
  { id: 'AC510', type: 'assistant_chief', station: '10', label: 'Asst. Chief 510' },
  { id: 'AC513', type: 'assistant_chief', station: '13', label: 'Asst. Chief 513' },
  { id: 'AC518', type: 'assistant_chief', station: '18', label: 'Asst. Chief 518' },
  { id: 'AC520', type: 'assistant_chief', station: '20', label: 'Asst. Chief 520' },
] as const;

export type CommandApparatusType = (typeof COMMAND_APPARATUS)[number]['type'];

export const ALL_APPARATUS = [...APPARATUS, ...COMMAND_APPARATUS] as const;

export type ApparatusType = (typeof APPARATUS)[number]['type'];

export const APPARATUS_NAMES = APPARATUS.map((a) => a.id) as unknown as readonly string[];

/** Returns true if the unit ID represents a flex unit (F or BF suffix). */
export function isFlexUnit(id: string): boolean {
  return /(?:B?F)$/.test(id);
}

/** Extract station number from a unit ID (e.g. 'E518' → '18', 'TW513' → '13'). */
export function getStationFromId(id: string): string {
  const match = id.match(/(\d{2,3})(?:B?F)?$/);
  if (!match) return 'other';
  const num = match[1];
  // Map 510→10, 513→13, 518→18, 520→20
  if (num.length === 3 && num.startsWith('5')) return num.slice(1);
  return num;
}

export function getApparatusType(name: string): string | undefined {
  return (APPARATUS.find((a) => a.id === name)?.type ?? COMMAND_APPARATUS.find((a) => a.id === name)?.type) as
    | ApparatusType
    | CommandApparatusType
    | undefined;
}

export function getApparatusLabel(id: string): string {
  return APPARATUS.find((a) => a.id === id)?.label ?? COMMAND_APPARATUS.find((a) => a.id === id)?.label ?? id;
}

/** Station display order and labels */
export const STATIONS = [
  { id: '10', label: 'FS10 - Dime House' },
  { id: '13', label: 'FS13 - Midtown' },
  { id: '18', label: 'FS18 - Retirement Home' },
  { id: '20', label: 'FS20 - Parkway Express' },
] as const;

/** Group apparatus by type for UI display */
export function getApparatusByType(): Record<string, (typeof APPARATUS)[number][]> {
  const groups: Record<string, (typeof APPARATUS)[number][]> = {};
  for (const a of APPARATUS) {
    if (!groups[a.type]) groups[a.type] = [];
    groups[a.type].push(a);
  }
  return groups;
}
